import styled from 'styled-components';
import React, { FC, memo, useContext, useMemo } from 'react';
import { media } from 'src/styles/designs';
import { VideoFavouritesContainer } from 'src/modules/materials/components/ShowcasePage/containers/VideoFavouritesContainer';
import { BusinessCasesContainer } from 'src/modules/materials/components/ShowcasePage/containers/BusinessCasesContainer';
import { ProtocolsContainer } from 'src/modules/materials/components/ShowcasePage/containers/ProtocolsContainer';
import { SearchResultsContainer } from 'src/modules/materials/components/ShowcasePage/containers/SearchResultsContainer';
import { withSilentErrorBoundary } from 'src/utils/WithSilentErrorBoundary';
import { useKeepData } from 'src/hooks/useKeepData';
import { AnimatePresence, motion } from 'framer-motion';
import { makeTransition } from 'src/utils/motion/transitions';
import { useUpdateEffect } from 'react-use';
import { HeroLoaderFillLayer } from 'src/components/common/Loader';
import { useTranslate } from 'react-t7e';
import {
  ShowcaseSearchVariables,
  ShowcaseSection,
  ShowcaseSectionData,
} from 'src/modules/materials/models/showcase';
import { VideoHeroListQueryContainer } from 'src/modules/materials/components/ShowcasePage/containers/VideoHeroListQueryContainer';
import { SliderQueryContainer } from 'src/modules/materials/components/ShowcasePage/containers/SliderQueryContainer';
import { SearchFilters } from 'src/services/search';
import { UseQueryResult } from 'react-query';
import {
  checkActiveFilters,
  useSearchItemsByFiltersQuery,
} from 'src/modules/materials/hooks/useSearchItemsByFiltersQuery';
import { useFavoriteVideosQuery } from 'src/modules/user/hooks/useFavouriteVideosQuery';
import { ShowcaseContext } from 'src/modules/materials/providers/ShowcaseContext';
import { shallowEqual } from 'fast-equals';
import { useDelayedValue } from 'src/hooks/useDelayedValue';

export const ResultsContainer = styled.main`
  position: relative;
  padding-bottom: 20px;
  --results-right-margin: var(--constraint-container-left-margin);
  --results-left-margin: var(--constraint-container-right-margin);
  min-height: calc(var(--vh) * 33);

  ${media.w.greaterThanOrEqual.desktop} {
    padding: 42px 120px;
    --results-right-margin: calc(
      120px + var(--constraint-container-right-margin)
    );
    --results-left-margin: 120px;
    min-height: 0;
  }
`;

const Title = styled.h2`
  font-weight: 700;
  font-size: 18px;
  line-height: 1.3;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.text.palette.primary};

  margin-top: -16px;
  margin-bottom: 26px;
  ${media.w.greaterThanOrEqual.desktop} {
    margin-bottom: 34px;
  }
`;

const Section = withSilentErrorBoundary(
  memo<{ section: ShowcaseSectionData }>(function Section({ section }) {
    switch (section.type) {
      case ShowcaseSection.List:
        return <SliderQueryContainer section={section} />;
      case ShowcaseSection.Favorites:
        return <VideoFavouritesContainer section={section} />;
      case ShowcaseSection.BusinessCases:
        return <BusinessCasesContainer section={section} />;
      case ShowcaseSection.ProtocolTreatment:
        return <ProtocolsContainer section={section} />;
      case ShowcaseSection.HeroList:
        return <VideoHeroListQueryContainer section={section} />;
      default:
        return null;
    }
  }),
);

export const ShowcaseGallery: FC<{
  filters: SearchFilters;
  sectionsQuery: () => UseQueryResult<ShowcaseSectionData[]>;
}> = ({ filters, sectionsQuery }) => {
  const hasActiveFilters = checkActiveFilters(filters);
  const sectionsQueryResult = sectionsQuery();

  const favoriteVideos = useFavoriteVideosQuery();
  const { title, itemsToQuery, defaultFilters } = useContext(ShowcaseContext);

  const customHeading = useMemo(
    () =>
      sectionsQueryResult.data?.find((section) =>
        shallowEqual(section.searchQuery, filters),
      )?.name,
    [sectionsQueryResult.data, filters],
  );

  // TODO: find a better way to overwrite search filters
  const [queryFilters, variables] = useMemo((): [
    SearchFilters,
    ShowcaseSearchVariables | undefined,
  ] => {
    if (filters.favourites && favoriteVideos.data) {
      return [
        favoriteVideos.data.length
          ? { status: ['published'], id: favoriteVideos.data }
          : {},
        undefined,
      ];
    }

    if (filters.recent) {
      return [
        { ...filters, ...defaultFilters },
        { limit: 20, sort: ['-date_created'] },
      ];
    }

    return [{ ...filters, ...defaultFilters }, undefined];
  }, [filters, defaultFilters, favoriteVideos.data]);

  const searchQueryResults = useSearchItemsByFiltersQuery(queryFilters, {
    itemsToQuery,
    ...variables,
  });

  const isFetchingData =
    sectionsQueryResult.isFetching || searchQueryResults.isFetching;

  const sections = useKeepData(sectionsQueryResult, isFetchingData);

  const showSections = useKeepData(
    !hasActiveFilters && sections?.data != null,
    isFetchingData,
  );

  const results = useKeepData(searchQueryResults.data, isFetchingData);

  useUpdateEffect(() => {
    window.scroll({
      top: 0,
      behavior: 'smooth',
    });
  }, [isFetchingData]);

  const isFetchingDataDelayed = useDelayedValue(isFetchingData, 600);
  const delayedFilters = useKeepData(filters, isFetchingData);
  const delayedHeading = useKeepData(customHeading, isFetchingData);

  const { _ } = useTranslate();

  return (
    <ResultsContainer>
      {title && <Title>{title}</Title>}
      <AnimatePresence exitBeforeEnter presenceAffectsLayout>
        {isFetchingData && isFetchingDataDelayed ? (
          <HeroLoaderFillLayer />
        ) : (results && ~results.count) || customHeading ? (
          <SearchResultsContainer
            heading={delayedHeading ? _(delayedHeading) : undefined}
            filters={delayedFilters}
            results={results}
            key="search"
          />
        ) : showSections ? (
          <motion.div
            key="default"
            {...makeTransition({
              stagger: 0.05,
            })}
          >
            {sections?.data?.map((section) => (
              <motion.div
                key={section.name}
                {...makeTransition({
                  fade: true,
                  forStagger: true,
                })}
              >
                <Section section={section} />
              </motion.div>
            ))}
          </motion.div>
        ) : null}
      </AnimatePresence>
    </ResultsContainer>
  );
};
